import React, { useCallback, useEffect, useRef, useState } from "react";
import { TableDropDown } from "@solsys/solsys-reactjs";
import { gql, useQuery } from "@apollo/client";

const gqlQuery = gql`
  query serviceGroupDropDown($service_group_list_request: service_group_list_request) {
    service_group(service_group_list_request: $service_group_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
  }
`;

const mapEdges = (edges) =>
  (edges ?? []).map((item) => ({
    ...item,
    ID: item.id,
    Name: item.name,
  }));

const ServiceGroupDropDown = ({ selected, ...props }) => {
  selected = selected === -1 ? undefined : selected;

  const truncateLength = 25;

  const [requestState, setRequestState] = useState({
    page: 1,
    pageSize: 20,
    search: "",
  });
  const { page, pageSize, search } = requestState;

  const [accumulatedList, setAccumulatedList] = useState([]);
  const filtersKey = JSON.stringify(props.filters ?? []);
  const sortingKey = JSON.stringify(props.sorting ?? []);

  let variables = {
    service_group_list_request: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  let renderVariables = {
    service_group_list_request: {
      pagination: {
        limit: truncateLength,
        offset: 0,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  if (
    (Array.isArray(selected) && selected.length > 0) ||
    Number.isInteger(selected)
  ) {
    let array = [];
    if (props.multiple) {
      selected.forEach((v) => array.push(v));
    } else {
      array.push(selected);
    }
    renderVariables.service_group_list_request.filters.push({
      field: "id",
      operator: "in",
      value: `(${array.join(", ")})`,
    });
  }

  if (search && search !== "")
    variables.service_group_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${search}%`,
    });
  if (props.filters?.length > 0) {
    props.filters.forEach((f) =>
      variables.service_group_list_request.filters.push(f)
    );
  }
  if (props.sorting?.length > 0) {
    props.sorting.forEach(
      (s) => (variables.service_group_list_request.sorting = [s])
    );
  }
  let { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
    variables: renderVariables,
  });
  const renderLoading = renderObject.loading;
  const renderError = renderObject.error;
  const renderData = renderObject.data;

  useEffect(() => {
    renderRefetch();
  }, [selected, renderRefetch]);

  const renderEntityListResponse = renderData?.service_group;
  const renderEntityRaw = renderEntityListResponse?.edges ?? [];
  const renderEntity = renderEntityRaw.map(item => ({
    ...item,
    ID: item.id,
    Name: item.name
  }));

  const { refetch: refetchServiceGroup, loading, error, data } = useQuery(gqlQuery, {
    variables,
  });
  useEffect(() => {
    if (!props.disabled) refetchServiceGroup();
  }, [page, search, filtersKey, sortingKey, refetchServiceGroup, props.disabled]);

  useEffect(() => {
    setAccumulatedList([]);
    setRequestState((s) => ({ ...s, page: 1 }));
  }, [search, filtersKey, sortingKey]);

  useEffect(() => {
    if (loading) return;
    const edges = data?.service_group?.edges;
    if (!edges) return;
    const mapped = mapEdges(edges);
    setAccumulatedList((prev) => {
      if (page === 1) return mapped;
      const seen = new Set(prev.map((x) => Number(x.id)));
      const toAdd = mapped.filter((x) => !seen.has(Number(x.id)));
      return [...prev, ...toAdd];
    });
  }, [data, page, loading]);

  const totalCountRef = useRef(0);
  totalCountRef.current = data?.service_group?.totalCount ?? 0;
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedList.length;

  const onSearch = (event) => {
    let { value } = event.target;
    setRequestState((s) => ({
      ...s,
      search: value,
      page: 1,
    }));
  };

  const paginationChange = (newPage) => {
    setRequestState((s) => ({
      ...s,
      page: newPage,
    }));
  };

  const addMoreResultsHandler = useCallback(() => {
    if (props.disabled) return;
    setRequestState((s) => {
      if (accumulatedLenRef.current >= totalCountRef.current) return s;
      return { ...s, page: s.page + 1 };
    });
  }, [props.disabled]);

  const entityCount = data?.service_group?.totalCount ?? 0;

  if (error) console.error(`Service Group drop down data error : ${error.message}`);
  if (renderError)
    console.error(`Service Group drop down render error : ${renderError.message}`);

  const headers = [
    { label: "ID" },
    { label: "Name" }
  ];

  return (
    <TableDropDown
      { ...props }
      items={ accumulatedList }
      loading={ loading }
      renderLoading={ renderLoading }
      multiple={ props.multiple }
      searchEnabled={ props.searchEnabled }
      searchValue={ search }
      onSearch={ onSearch }
      selectValue={ selected }
      selectRender={ renderEntity }
      onSelect={ props.onChange || props.onSelect }
      dataKey={ "id" }
      identityColumnKey={ "ID" }
      renderColumnKey={ "Name" }
      headers={ headers }
      truncateLength={ truncateLength }
      entityCount={ entityCount }
      onPaginationChange={ paginationChange }
      addMoreResults={
        typeof props.addMoreResults === "function"
          ? props.addMoreResults
          : addMoreResultsHandler
      }
    />
  );
};

export default ServiceGroupDropDown;
